import React, { useState, useEffect, useMemo } from "react";
import { toast } from "react-toastify";
import { Typography, TextField, FormControl, Select, MenuItem } from "@material-ui/core";
import { ExpandMore } from "@material-ui/icons";
import { useFormik } from "formik";

import { useStore } from "@store/store";
import Header from "@components/header";
import ProfileSideBar from "@components/profile-sidebar";
import DeletePopup from "@components/deletePopup";
import PopupDialog from "@components/popupDialog";
import SearchIcon from "@assets/images/search.svg";
import {
  FETCH_CONNECTED_CUSTOMER_USERS,
  FETCH_CONNECTED_CUSTOMER_USERS_SUCCESS,
  FETCH_CONNECTED_CUSTOMER_USERS_FAILURE,
  ADD_CONNECTED_CUSTOMER_USER,
  ADD_CONNECTED_CUSTOMER_USER_SUCCESS,
  ADD_CONNECTED_CUSTOMER_USER_FAILURE,
  EDIT_CONNECTED_CUSTOMER_USER,
  EDIT_CONNECTED_CUSTOMER_USER_SUCCESS,
  EDIT_CONNECTED_CUSTOMER_USER_FAILURE,
  DELETE_CONNECTED_CUSTOMER_USER,
  DELETE_CONNECTED_CUSTOMER_USER_SUCCESS,
  DELETE_CONNECTED_CUSTOMER_USER_FAILURE,
  GET_PARENT_CUSTOMER,
  GET_PARENT_CUSTOMER_SUCCESS,
  GET_PARENT_CUSTOMER_FAILURE,
  RESEND_WELCOME_EMAIL_TO_CONNECTED_CUSTOMER_USER,
  RESEND_WELCOME_EMAIL_TO_CONNECTED_CUSTOMER_USER_SUCCESS,
  RESEND_WELCOME_EMAIL_TO_CONNECTED_CUSTOMER_USER_FAILURE
} from "@utils/actionTypes";
import { schema } from "@utils/schemas";
import { rowsPerPageVal } from "@utils/constant";
import validationSchema from "@utils/validationSchemas";
import { generatePassword } from "@utils/commonFunctions";
import { materialCommonStyles } from "@utils/materialCommonStyles";
import API from "@services/axios";
import AddUser from "./add-connected-customer-user";
import TableListing from "./table-listing";
import { ConnectedCustomerUsersStyle } from "./style";
import { useDebouncedEffect } from "@hooks/debounceEffect";

function ConnectedCustomerUsers() {
  const classes = ConnectedCustomerUsersStyle();
  const materialClasses = materialCommonStyles();
  const [openDeletePopup, setDeletePopup] = useState(false);
  const [getDeleteUser, setDeleteUser] = useState({});
  const [resendMailUser, setResendMailUser] = useState({});
  const [openResendPopup, setOpenResendPopup] = useState(false);
  const [openAddPopup, setOpenAddPopup] = useState(false);
  const [getEditUser, setEditUser] = useState(
    schema.addConnectedCustomerSchema
  );
  const [getEdit, setEdit] = useState(false);
  const [debounceSearch, setDebounceSearch] = useState("");
  const [page, setPage] = useState(0);
  const [order, setOrder] = useState("asc");
  const [orderBy, setOrderBy] = useState("name");
  const [rowsPerPage, setRowsPerPage] = useState(rowsPerPageVal);
  const [state, dispatch] = useStore();
  const [err, setError] = useState("");
  const [search, setSearch] = useState("");
  const [userStatusFilter, setUserStatusFilter] = useState("");

  const paramsGetUser = useMemo(() => {
    return {
      page: page + 1,
      size: rowsPerPage,
      ...(!!debounceSearch ? { search: debounceSearch } : {}),
      order: order !== "" ? order : "asc",
      orderBy: orderBy !== "" ? orderBy : "name",
      ...(userStatusFilter
        ? { filter: { status: userStatusFilter === "active" ? 1 : 0 } }
        : {}),
    };
  }, [page, rowsPerPage, debounceSearch, order, orderBy, userStatusFilter]);

  // API calling to get list of connected customers
  let getUsers = (payload = paramsGetUser) => {
    const params = payload;
    dispatch({ type: FETCH_CONNECTED_CUSTOMER_USERS });
    API.get("connectedCustomer", { params })
      .then((response) => {
        dispatch({
          type: FETCH_CONNECTED_CUSTOMER_USERS_SUCCESS,
          payload: response.data.data,
        });
      })
      .catch((err) => {
        dispatch({
          type: FETCH_CONNECTED_CUSTOMER_USERS_FAILURE,
          payload: err,
        });
      });
  };

  // API calling to add connected customers
  let addUser = (value) => {
    const data = {
      name: value.name,
      email: value.email,
      password: value.password,
      customerParentIds: value.parentCustomer.map(item => item.id),
      isMapCockpitAutoRefresh: value.isMapCockpitAutoRefresh ? 1 : 0,
    };
    dispatch({ type: ADD_CONNECTED_CUSTOMER_USER });
    API.post("connectedCustomer", data)
      .then((response) => {
        handleCloseAddPopup();
        getUsers();
        toast.success("Connected Customer User Added Successfully");
        dispatch({
          type: ADD_CONNECTED_CUSTOMER_USER_SUCCESS,
          payload: response.data.data,
        });
      })
      .catch((error) => {
        if (error.response.data.code === 409)
          setError(error.response?.data?.message);
        dispatch({ type: ADD_CONNECTED_CUSTOMER_USER_FAILURE, payload: error });
      });
  };

  // API calling to edit connected customers
  let editUser = (value) => {
    let data = {
      name: value.name,
      email: value.email,
      customerParentIds: value.parentCustomer.map(item => item.id),
      isMapCockpitAutoRefresh: value.isMapCockpitAutoRefresh ? 1 : 0,
    };
    data = {
      ...data,
      ...(!!value.password ? { password: value.password } : {}),
    };
    dispatch({ type: EDIT_CONNECTED_CUSTOMER_USER });
    API.put(`connectedCustomer/${value.id}`, data)
      .then((response) => {
        handleCloseAddPopup();
        getUsers();
        toast.success("Connected Customer User Updated Successfully");
        dispatch({
          type: EDIT_CONNECTED_CUSTOMER_USER_SUCCESS,
          payload: response.data.data,
        });
      })
      .catch((error) => {
        if (error.response.data.code === 409)
          setError(error.response?.data?.message);
        dispatch({
          type: EDIT_CONNECTED_CUSTOMER_USER_FAILURE,
          payload: error,
        });
      });
  };

  useEffect(() => {
    getUsers();
    getParentCustomerList();
  }, []);

  // API calling to parent customers list for add/edit connected customer user
  const getParentCustomerList = () => {
    dispatch({ type: GET_PARENT_CUSTOMER });
    API.get("master/parentCustomers")
      .then((response) => {
        dispatch({
          type: GET_PARENT_CUSTOMER_SUCCESS,
          payload: response.data.data,
        });
      })
      .catch((err) => {
        dispatch({ type: GET_PARENT_CUSTOMER_FAILURE, payload: err });
      });
  };

  // API calling to delete connected customers
  const handleDelete = () => {
    dispatch({ type: DELETE_CONNECTED_CUSTOMER_USER });
    API.delete(`connectedCustomer/${getDeleteUser.id}`)
      .then((response) => {
        setDeletePopup(false);
        getUsers();
        toast.success("Connected Customer User Deleted Successfully");
        dispatch({
          type: DELETE_CONNECTED_CUSTOMER_USER_SUCCESS,
          payload: response.data.data,
        });
      })
      .catch((error) => {
        setError("You can't delete this record");
        dispatch({
          type: DELETE_CONNECTED_CUSTOMER_USER_FAILURE,
          payload: error,
        });
      });
  };

  // API calling to resend welcome mail to connected customer user
  const handleResendMail = () => {
    dispatch({ type: RESEND_WELCOME_EMAIL_TO_CONNECTED_CUSTOMER_USER });
    setOpenResendPopup(false);
    API.post(`connectedCustomer/${resendMailUser.id}/resendWelcomeEmail`, {
      password: generatePassword()
    })
      .then((response) => {
        getUsers();
        toast.success("Welcome email sent successfully!");
        dispatch({
          type: RESEND_WELCOME_EMAIL_TO_CONNECTED_CUSTOMER_USER_SUCCESS,
        });
      })
      .catch((error) => {
        toast.error("Failed to send welcome email. Please try again.");
        dispatch({
          type: RESEND_WELCOME_EMAIL_TO_CONNECTED_CUSTOMER_USER_FAILURE,
        });
      });
  };

  const handleDeletePopup = (user) => {
    setDeleteUser(user);
    setDeletePopup(true);
    setError("");
  };

  const handleAddPopup = (user) => {
    if (user) {
      const listOfParentCustomerIds = user.customerParentIds ? JSON.parse(user.customerParentIds) : [];
      const data = {
        id: user.id,
        name: user.name,
        email: user.email,
        password: "",
        confirmPassword: "",
        parentCustomer: state?.common?.parentCustomers
          ? state?.common?.parentCustomers.filter(pc => listOfParentCustomerIds.includes(pc.id))
          : [],
        isMapCockpitAutoRefresh: user.isMapCockpitAutoRefresh
      };
      setEdit(true);
      // eslint-disable-next-line
      setEditUser(data);
    }
    setOpenAddPopup(true);
  };

  const handleResendPopup = user => {
    setResendMailUser(user);
    setOpenResendPopup(true);
    setError("");
  }

  const handleCloseAddPopup = () => {
    setOpenAddPopup(false);
    setEdit(false);
    setEditUser(schema.addConnectedCustomerSchema);
    setError("");
    formik.handleReset();
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
    let payload = {
      ...paramsGetUser,
      page: newPage + 1,
    };
    getUsers(payload);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
    let payload = {
      ...paramsGetUser,
      page: 1,
      size: parseInt(event.target.value, 10),
    };
    getUsers(payload);
  };
  const formik = useFormik({
    enableReinitialize: true,
    initialValues: getEditUser,
    validationSchema: getEdit
      ? validationSchema.editConnectedCustomerValidationSchema
      : validationSchema.addConnectedCustomerValidationSchema,
    onSubmit: (value) => {
      getEdit ? editUser(value) : addUser(value);
    },
  });

  const handleSorting = (event, property) => {
    const isAsc = orderBy === property && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);
    resetPagination();
    let payload = {
      ...paramsGetUser,
      page: 1,
      size: rowsPerPageVal,
      order: isAsc ? "desc" : "asc",
      orderBy: property,
    };
    getUsers(payload);
  };

  const resetPagination = () => {
    setPage(0);
    setRowsPerPage(rowsPerPageVal);
  };

  useDebouncedEffect(
    () => {
      setDebounceSearch(search);
      resetPagination();
      const { search: _existingSearch, ...restParams } = paramsGetUser;
      let payload = {
        ...restParams,
        page: 1,
        size: rowsPerPageVal,
        ...(search ? { search } : {}),
      };
      getUsers(payload);
    },
    1000,
    [search]
  );

  const handleSearch = (event) => {
    setSearch(event.target.value.trimStart());
  };

  const handleStatusFilterChange = (event) => {
    const selectedStatus = event.target.value;
    setUserStatusFilter(selectedStatus);
    resetPagination();
    const { filter: _existingFilter, ...restParams } = paramsGetUser;
    let payload = {
      ...restParams,
      page: 1,
      size: rowsPerPageVal,
      ...(selectedStatus
        ? { filter: { status: selectedStatus === "active" ? 1 : 0 } }
        : {}),
    };
    getUsers(payload);
  };

  return (
    <>
      <Header />
      <div className={classes.CustomerListingWrapper}>
        <div className="setting-page wrapper">
          <div className="inner-page">
            <Typography variant="h1">Settings</Typography>
            <div className="setting-row-wrapper">
              <div className="left-sidebar">
                <ProfileSideBar />
              </div>
              <div className="right-content">
                <div className="white-card right-content-inner">
                  <div className={classes.innerPageTopBlock}>
                    <div className="left-block">
                      <Typography variant="h1">
                        Connected Customer Users
                      </Typography>
                    </div>
                    <div className="right-block">
                      <div className="right-block-inner">
                        <div className="search-wrapper">
                          <div className="form-gourp">
                            <TextField
                              id="search-request"
                              placeholder="Search Connected Customer Users"
                              variant="outlined"
                              type="search"
                              InputProps={{
                                endAdornment: (
                                  <img src={SearchIcon} alt="Search" />
                                ),
                              }}
                              onChange={handleSearch}
                              value={search}
                            />
                          </div>
                        </div>
                        <div className="modal-wrapper">
                          <div className="btn-wrapper">
                            <FormControl
                              variant="outlined"
                              size="small"
                              className={classes.formControl}
                            >
                              <Select
                                value={userStatusFilter}
                                onChange={handleStatusFilterChange}
                                displayEmpty
                                className={materialClasses.customSelect}
                                MenuProps={{
                                  classes: { paper: materialClasses.customSelect },
                                }}
                                IconComponent={() => <ExpandMore />}
                              >
                                <MenuItem value="">Choose Status</MenuItem>
                                <MenuItem value="active">Active</MenuItem>
                                <MenuItem value="inactive">Inactive</MenuItem>
                              </Select>
                            </FormControl>
                          </div>
                        </div>
                        <div className="modal-wrapper">
                          <div className="btn-wrapper">
                            <AddUser
                              handleClickOpen={() => handleAddPopup()}
                              handleClose={handleCloseAddPopup}
                              open={openAddPopup}
                              formik={formik}
                              isEdit={getEdit}
                              error={err}
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                  <TableListing
                    handleOpen={(user) => handleDeletePopup(user)}
                    handleEdit={(editUser) => handleAddPopup(editUser)}
                    handleResend={user => handleResendPopup(user)}
                    handleSorting={(e, property) => handleSorting(e, property)}
                    orderBy={orderBy}
                    order={order}
                    page={page}
                    handleChangePage={handleChangePage}
                    rowsPerPage={rowsPerPage}
                    handleChangeRowsPerPage={handleChangeRowsPerPage}
                  />
                  <DeletePopup
                    open={openDeletePopup}
                    handleClose={() => setDeletePopup(false)}
                    handleDelete={(deleteUser) => handleDelete(deleteUser)}
                    deleteUser={getDeleteUser.name}
                    loading={state.connectedUser.deletingUser}
                    error={err}
                  />
                  <PopupDialog
                    open={openResendPopup}
                    onClose={() => setOpenResendPopup(false)}
                    title="Welcome this customer?"
                    content="A welcome email will be sent to the customer right away. Are you sure you want to continue?"
                    cancelLabel="Cancel"
                    onCancel={() => setOpenResendPopup(false)}
                    confirmLabel="Send"
                    onConfirm={handleResendMail}
                    showCloseIcon={false}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
export default ConnectedCustomerUsers;
